<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { ScoreResult } from '@/features/scoring/lib/types'
import { useScoring } from '@/features/scoring/composables/useScoring'
import { buildScoreResult, isScoreInputsComplete } from '@/features/scoring/lib/buildScoreResult'
import PushUpsAccordion from '@/features/scoring/components/PushUpsAccordion.vue'
import CoreAccordion from '@/features/scoring/components/CoreAccordion.vue'
import CardioAccordion from '@/features/scoring/components/CardioAccordion.vue'
import WHtRAccordion from '@/features/scoring/components/WHtRAccordion.vue'
import type { CalcSubject, MemberCompletionState, TestSessionDraft, UFPMScoreEntry } from '../lib/types'
import {
  draftToScoringState,
  hasDraftContent,
  scoringStateToDraft,
} from '../lib/scoreEntryToDraft'
import { scoreEntryToResult } from '../lib/scoreEntryToResult'
import { scoreBandClass } from '../lib/scoreDisplay'
import { buildScoreHistoryEntry } from '../lib/scoreHistoryEntry'
import { guestLabel } from '../lib/testSession'
import { SCORING_FIELD_PLACEHOLDER } from '@/features/scoring/lib/scoringInput'

const props = withDefaults(defineProps<{
  subject: CalcSubject
  draft?: TestSessionDraft
  completion?: MemberCompletionState
  savedScore?: UFPMScoreEntry
}>(), {
  completion: 'empty',
})

const emit = defineEmits<{
  saved: [subjectId: string, score: ScoreResult, entry: UFPMScoreEntry]
  'update-draft': [subjectId: string, draft: TestSessionDraft]
  'clear-draft': [subjectId: string]
}>()

const { profile, pushUps, core, cardio, whtr, result, componentScores, clear } = useScoring({
  autoSave: false,
  setGlobalScore: false,
  restorePersistedInputs: false,
})

const isSaving = ref(false)
const savedLocally = ref(false)

const isHypothetical = computed(() => props.subject.kind === 'hypothetical')
const isLocked = computed(() => !isHypothetical.value && props.completion === 'saved')

const subjectId = computed<string | null>(() => {
  if (props.subject.kind === 'member') return props.subject.member.id
  if (props.subject.kind === 'guest') return props.subject.guest.id
  return null
})

const subjectName = computed(() => {
  if (props.subject.kind === 'member') return props.subject.member.fullName
  if (props.subject.kind === 'guest') return guestLabel(props.subject.guest)
  return ''
})

const anyExempt = computed(() => pushUps.exempt || core.exempt || cardio.exempt || whtr.exempt)

const liveComposite = computed(() => {
  const scored = buildScoreResult({ profile, pushUps, core, cardio, whtr })
  return scored?.composite ?? null
})

const inputsComplete = computed(() =>
  isScoreInputsComplete({ profile, pushUps, core, cardio, whtr })
)

const canSave = computed(() => !isLocked.value && !isSaving.value && inputsComplete.value)

function entrySexToProfile(sex: string | undefined): 'male' | 'female' | '' {
  return sex === 'male' || sex === 'female' ? sex : ''
}

const savedResult = computed(() => {
  if (isHypothetical.value) return result.value
  if (props.completion !== 'saved') return result.value
  if (result.value) return result.value
  if (props.subject.kind === 'guest') {
    const entry = props.savedScore
    return entry
      ? scoreEntryToResult(entry, { age: Number(entry.age || 0), sex: entrySexToProfile(entry.sex) })
      : null
  }
  if (props.subject.kind === 'member') {
    const latest = props.subject.member.history?.[0]
    return latest ? scoreEntryToResult(latest, props.subject.member) : null
  }
  return null
})

const savedScoreClass = computed(() =>
  savedResult.value
    ? scoreBandClass(savedResult.value.cat, savedResult.value.composite)
    : ''
)

const resultCardVisible = computed(() =>
  isHypothetical.value ? result.value != null : isLocked.value
)

function todayISO(): string {
  const d = new Date()
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

function applyMemberProfile() {
  if (props.subject.kind !== 'member') return
  const sex = props.subject.member.sex
  if (sex === 'male' || sex === 'female') profile.sex = sex
}

function applyDraft(draft?: TestSessionDraft) {
  if (!draft) return
  const state = draftToScoringState(draft)
  Object.assign(profile, state.profile)
  Object.assign(pushUps, state.pushUps)
  Object.assign(core, state.core)
  Object.assign(cardio, state.cardio)
  Object.assign(whtr, state.whtr)
}

function syncDraftToSession() {
  if (isHypothetical.value) return
  const id = subjectId.value
  if (!id || props.completion === 'saved' || savedLocally.value) return
  const draft = scoringStateToDraft({ profile, pushUps, core, cardio, whtr })
  if (hasDraftContent(draft)) emit('update-draft', id, draft)
  else emit('clear-draft', id)
}

async function handleSave() {
  if (isHypothetical.value || !canSave.value || isSaving.value) return
  const scored = buildScoreResult({ profile, pushUps, core, cardio, whtr })
  if (!scored) {
    window.showToast?.('Enter a valid age from 17 to 70.')
    return
  }

  isSaving.value = true
  result.value = scored

  const id = subjectId.value as string
  const entry = buildScoreHistoryEntry(scored, {
    id: Date.now(),
    recordedDate: todayISO(),
    memberId: id,
    memberName: subjectName.value,
  }, { pushUps, core, cardio, whtr })

  if (props.subject.kind === 'member') {
    // Roster member: score goes to unit history via the vanilla bridge.
    window.ufpmTools?.selectMember?.(id)
    if (window.ufpmTools?.saveScoreForSelectedMember(entry)) {
      savedLocally.value = true
      window.showToast?.(`Score ${scored.composite.toFixed(1)} saved for ${subjectName.value}`)
      emit('saved', id, scored, entry)
    } else {
      window.showToast?.('Could not save score for this member.')
    }
  } else {
    // Guest: score lives only on the test session — never touches the roster.
    savedLocally.value = true
    window.showToast?.(`Score ${scored.composite.toFixed(1)} saved for ${subjectName.value} (session only)`)
    emit('saved', id, scored, entry)
  }
  isSaving.value = false
}

function handleCalculate() {
  const scored = buildScoreResult({ profile, pushUps, core, cardio, whtr })
  if (!scored) {
    window.showToast?.('Enter a valid age from 17 to 70.')
    return
  }
  result.value = scored
}

function handlePrimaryAction() {
  if (isHypothetical.value) handleCalculate()
  else void handleSave()
}

function handleClear() {
  clear()
  applyMemberProfile()
  result.value = null
  if (subjectId.value) emit('clear-draft', subjectId.value)
}

onMounted(() => {
  applyMemberProfile()
  if (!isLocked.value) applyDraft(props.draft)
})

watch(() => props.completion, (state) => {
  if (state !== 'saved') savedLocally.value = false
})

watch(
  () => [profile, pushUps, core, cardio, whtr] as const,
  () => {
    if (isHypothetical.value) {
      // A stale result card would misstate the category — recalc explicitly.
      result.value = null
      return
    }
    syncDraftToSession()
  },
  { deep: true },
)

onBeforeUnmount(() => syncDraftToSession())
</script>

<template>
  <div class="ufpm-calc-panel">
    <div v-if="isLocked" class="ufpm-calc-notice ufpm-calc-notice--saved">
      {{ subject.kind === 'guest'
        ? 'Score saved for this session only — it is not written to unit history and clears with the session.'
        : 'Score saved for this session. Select another member on the Test tab or clear the session to retest.' }}
    </div>

    <div class="ufpm-calc-body" :class="{ 'ufpm-calc-body--locked': isLocked }">
      <div class="ufpm-calc-profile">
        <div class="field">
          <label>Sex</label>
          <div class="toggle-group">
            <button
              :class="['toggle-btn', { active: profile.sex === 'male' }]"
              type="button"
              :disabled="isLocked"
              @click="profile.sex = 'male'"
            >Male</button>
            <button
              :class="['toggle-btn', { active: profile.sex === 'female' }]"
              type="button"
              :disabled="isLocked"
              @click="profile.sex = 'female'"
            >Female</button>
          </div>
        </div>
        <div class="field sm">
          <label>Age</label>
          <input
            v-model="profile.age"
            type="text"
            inputmode="numeric"
            :placeholder="SCORING_FIELD_PLACEHOLDER"
            aria-label="Age"
            :disabled="isLocked"
          />
        </div>
      </div>

      <div v-show="anyExempt" class="alert alert-info ufpm-calc-alert">
        Exempted components are excluded from scoring. The composite is calculated from remaining components, redistributed proportionally.
      </div>

      <WHtRAccordion
        v-model:waist="whtr.waist"
        v-model:height="whtr.height"
        v-model:exempt="whtr.exempt"
        :component-score="componentScores.whtr"
      />
      <PushUpsAccordion
        v-model:type="pushUps.type"
        v-model:reps="pushUps.reps"
        v-model:exempt="pushUps.exempt"
        :component-score="componentScores.pu"
      />
      <CoreAccordion
        v-model:type="core.type"
        v-model:reps="core.reps"
        v-model:plankMin="core.plankMin"
        v-model:plankSec="core.plankSec"
        v-model:exempt="core.exempt"
        :component-score="componentScores.core"
      />
      <CardioAccordion
        v-model:type="cardio.type"
        v-model:runMin="cardio.runMin"
        v-model:runSec="cardio.runSec"
        v-model:shuttles="cardio.shuttles"
        v-model:exempt="cardio.exempt"
        :component-score="componentScores.cardio"
      />
    </div>

    <footer class="ufpm-calc-actions">
      <div v-if="liveComposite != null && !isLocked" class="ufpm-calc-actions-live">
        <span class="ufpm-calc-actions-live__label">Composite</span>
        <span class="ufpm-calc-actions-live__value">{{ liveComposite.toFixed(1) }}</span>
      </div>
      <button
        class="btn btn-primary"
        type="button"
        :disabled="isHypothetical ? !inputsComplete : !canSave"
        @click="handlePrimaryAction"
      >{{ isHypothetical ? 'Calculate Score' : 'Save Score' }}</button>
      <button
        class="btn btn-secondary"
        type="button"
        :disabled="isLocked"
        @click="handleClear"
      >Clear</button>
    </footer>

    <div v-if="resultCardVisible && savedResult" class="card ufpm-calc-result">
      <div class="card-title">{{ isHypothetical ? 'Result' : 'Saved Score' }}</div>
      <div class="ufpm-profile-id-row">
        <span class="ufpm-profile-score-chip" :class="savedScoreClass">{{ savedResult.composite.toFixed(1) }}</span>
        <span class="ufpm-profile-name" :class="savedScoreClass">{{ savedResult.cat }}</span>
      </div>
      <div class="ufpm-hcl-breakdown">
        PU: {{ savedResult.puEx ? 'EXM' : savedResult.puScore.toFixed(1) }} |
        Core: {{ savedResult.coreEx ? 'EXM' : savedResult.coreScore.toFixed(1) }} |
        Cardio: {{ savedResult.cardioEx ? 'EXM' : savedResult.cardioScore.toFixed(1) }} |
        WHtR: {{ savedResult.whtrEx ? 'EXM' : savedResult.whtrScore.toFixed(1) }}
      </div>
    </div>
  </div>
</template>
